import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authFetch } from 'shared/api/authFetch';
import { admin_apiConfig } from 'shared/config/config';
import {
  getViewAs, setViewAs, subscribeTriedAction, subscribeViewAs,
} from 'shared/api/viewAsSession';
import { useCurrentUserAccess } from 'shared/session/useCurrentUserRole';
import TriedActionDialog from './TriedActionDialog';

const ViewAsContext = createContext(null);

// Queries the server answers as the viewer. The rest of the shell (the Superadmin's
// access, chats, drafts) stays loaded, so a draft being previewed stays selected.
const VIEWER_QUERIES = new Set([
  'ontologyViewsRuntime', 'ontologyViewRuntime', 'resolvedViewDataBatch', 'resolvedViewDataSource',
  'objectRelationships', 'objectRelationshipsTwoHop', 'ontologyViewObject', 'ontologyViewObjectSearch',
  'ontologyActionRuntime', 'views',
]);
const resetViewerQueries = (queryClient) => queryClient.resetQueries({
  predicate: (query) => VIEWER_QUERIES.has(query.queryKey?.[0]),
});

export const DEVICES = {
  desktop: null,
  tablet: { width: 768, height: 1024 },
  phone: { width: 390, height: 844 },
};

/** The Superadmin's own shell becomes the role's or person's until they exit. */
export function ViewAsProvider({ children }) {
  const viewer = useSyncExternalStore(subscribeViewAs, getViewAs, () => null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: access } = useCurrentUserAccess();
  const [tried, setTried] = useState(null);
  useEffect(() => subscribeTriedAction(setTried), []);

  // A stale tab (signed in as someone else, or no longer a Superadmin) drops it.
  useEffect(() => {
    if (viewer && access && !access.isSuperadmin) {
      setViewAs(null);
      void resetViewerQueries(queryClient);
    }
  }, [viewer, access, queryClient]);

  const start = useCallback(async (target, { path = '/app/home' } = {}) => {
    const response = await authFetch(`${admin_apiConfig.baseURL}/api/organization/view-as`, {
      method: 'POST',
      body: JSON.stringify({ target }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body.viewer) {
      throw new Error(typeof body.detail === 'string' ? body.detail : "Couldn't open their view. Try again.");
    }
    const previous = getViewAs();
    setViewAs({
      ...body.viewer,
      returnTo: previous?.returnTo || `${location.pathname}${location.search}`,
      device: previous?.device || 'desktop',
    });
    // The apps and their data were read as someone else.
    await resetViewerQueries(queryClient);
    navigate(path);
  }, [location.pathname, location.search, navigate, queryClient]);

  const exit = useCallback(async () => {
    const returnTo = getViewAs()?.returnTo || '/app/home';
    setViewAs(null);
    await resetViewerQueries(queryClient);
    navigate(returnTo);
  }, [navigate, queryClient]);

  const setDevice = useCallback((device) => {
    const current = getViewAs();
    if (current && device in DEVICES) setViewAs({ ...current, device });
  }, []);

  const canViewAs = Boolean(access?.isSuperadmin);
  const selfProfileId = access?.profileId || null;
  const value = useMemo(
    () => ({ viewer, start, exit, setDevice, canViewAs, selfProfileId }),
    [viewer, start, exit, setDevice, canViewAs, selfProfileId],
  );
  return <ViewAsContext.Provider value={value}>
    {children}
    {tried && <TriedActionDialog tried={tried} onClose={() => setTried(null)} />}
  </ViewAsContext.Provider>;
}

/** Null outside the workspace shell (the public demo, tests without a provider). */
export function useViewAs() {
  return useContext(ViewAsContext);
}

export const viewerLabel = (viewer) => viewer?.person_name || viewer?.role_name || 'Someone';

/** The workspace access of the person viewed, in the shell's gating shape. */
export function viewerAccess(viewer, own) {
  if (!viewer) return own;
  const access = viewer.workspace_access || 'apps';
  return {
    role: viewer.role_slug || null,
    workspaceAccess: access,
    platformTier: access === 'apps' ? 'worker' : 'admin',
    isSuperadmin: access === 'administrator',
    roleId: viewer.role_id || null,
    profileId: viewer.person_id || null,
    organizationId: own?.organizationId || null,
  };
}

// A frame's name survives its own navigation, unlike a query parameter.
export const VIEW_AS_FRAME_NAME = 'lineside-view-as-frame';

/** The device frame renders the same shell in a nested page: it must not nest again. */
export function isViewAsFrame() {
  return typeof window !== 'undefined' && window.name === VIEW_AS_FRAME_NAME;
}
